import React, { useState } from 'react';
import { Lock, Globe } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const ACCESS_CODE_FULL = 'ECO123$';
const ACCESS_CODE_DEMO = 'Ecoprez';
const STORAGE_KEY = 'ecolunch_access';
const ACCESS_TYPE_KEY = 'ecolunch_access_type';

const DEMO_ALLOWED_PATHS = ['/presentation-ecolunch', '/ecosysteme-portails', '/organigramme'];

const T = {
  fr: {
    enterCode: "Entrez le code d'accès",
    placeholder: "Code d'accès",
    submit: "Accéder",
    invalid: "Code invalide",
    confidential: "Document Confidentiel",
    nda: "L'accès à ce plan d'affaires est strictement confidentiel et réservé aux destinataires autorisés. Toute consultation implique l'acceptation d'une obligation de non-divulgation (NDA). La reproduction, la distribution ou la diffusion de ce contenu sans autorisation écrite préalable d'EcoTech Système est interdite.",
    copyright: "Tous droits réservés",
    portalsTitle: "Portails EcoTech",
    portalsSub: "Accédez à nos différentes plateformes",
    presentation: "Présentation",
    presentationSub: "Découvrez EcoLunch",
    ecosystem: "Écosystème",
    ecosystemSub: "Nos portails intégrés",
    orgchart: "Organigramme",
    logout: "Utiliser un autre code d'accès",
  },
  en: {
    enterCode: "Enter access code",
    placeholder: "Access code",
    submit: "Access",
    invalid: "Invalid code",
    confidential: "Confidential Document",
    nda: "Access to this business plan is strictly confidential and reserved for authorized recipients. Any consultation implies acceptance of a non-disclosure obligation (NDA). Reproduction, distribution, or sharing of this content without prior written authorization from EcoTech Système is prohibited.",
    copyright: "All rights reserved",
    portalsTitle: "EcoTech Portals",
    portalsSub: "Access our different platforms",
    presentation: "Presentation",
    presentationSub: "Discover EcoLunch",
    ecosystem: "Ecosystem",
    ecosystemSub: "Our integrated portals",
    orgchart: "Org Chart",
    logout: "Use a different access code",
  },
};

function LangToggle() {
  const { language, setLanguage } = useLanguage();
  return (
    <div className="absolute top-4 right-4 z-20 inline-flex items-center gap-2 bg-gray-900/80 backdrop-blur-md border border-gray-700 rounded-full px-3 py-1.5 shadow-lg" data-testid="gate-lang-toggle">
      <Globe className="h-3 w-3 text-gray-400" />
      <button
        type="button"
        onClick={() => setLanguage('fr')}
        data-testid="gate-lang-fr"
        className={`text-xs font-bold px-2 py-0.5 rounded-full transition-all ${language === 'fr' ? 'bg-lime-500 text-black' : 'text-gray-400 hover:text-white'}`}
      >
        FR
      </button>
      <button
        type="button"
        onClick={() => setLanguage('en')}
        data-testid="gate-lang-en"
        className={`text-xs font-bold px-2 py-0.5 rounded-full transition-all ${language === 'en' ? 'bg-lime-500 text-black' : 'text-gray-400 hover:text-white'}`}
      >
        EN
      </button>
    </div>
  );
}

function getStoredAccess() {
  const storedAccess = sessionStorage.getItem(STORAGE_KEY);
  const storedType = sessionStorage.getItem(ACCESS_TYPE_KEY);
  if (storedAccess === 'true' && storedType) {
    return { unlocked: true, accessType: storedType };
  }
  return { unlocked: false, accessType: null };
}

export default function GateKeeper({ children }) {
  const location = useLocation();
  const { language } = useLanguage();
  const t = T[language];

  const initial = getStoredAccess();
  const [unlocked, setUnlocked] = useState(initial.unlocked);
  const [accessType, setAccessType] = useState(initial.accessType);
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code === ACCESS_CODE_FULL) {
      sessionStorage.setItem(STORAGE_KEY, 'true');
      sessionStorage.setItem(ACCESS_TYPE_KEY, 'full');
      setAccessType('full');
      setUnlocked(true);
    } else if (code === ACCESS_CODE_DEMO) {
      sessionStorage.setItem(STORAGE_KEY, 'true');
      sessionStorage.setItem(ACCESS_TYPE_KEY, 'demo');
      setAccessType('demo');
      setUnlocked(true);
    } else {
      setError(true);
      setTimeout(() => setError(false), 1500);
    }
  };

  const currentPath = location.pathname;
  const isAllowedDemoPath = DEMO_ALLOWED_PATHS.includes(currentPath);

  if (unlocked) {
    if (accessType === 'demo') {
      if (isAllowedDemoPath) {
        return children;
      }
      return <DemoPortal />;
    }
    return children;
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative" style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_foodtech-finplan/artifacts/ye5d9g73_image.png')", backgroundSize: '180px', backgroundRepeat: 'repeat', backgroundPosition: 'center' }}>
      <div className="absolute inset-0 bg-black/85" />
      <LangToggle />
      <div className="relative z-10 w-full max-w-md" data-testid="gate-keeper">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_app-charge/artifacts/151hv25f_EcoTecH_Logo%20short.jpg"
            alt="EcoTech Système"
            className="h-32 mx-auto mb-4 rounded-xl drop-shadow-[0_0_30px_rgba(168,213,48,0.6)]"
          />
        </div>
        <form onSubmit={handleSubmit} className="bg-gray-900/90 backdrop-blur-md border-2 border-gray-700 rounded-2xl p-8 shadow-[0_0_40px_rgba(0,0,0,0.5)]">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="h-5 w-5 text-lime-400" />
            <p className="text-sm text-gray-300 font-medium">{t.enterCode}</p>
          </div>
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t.placeholder}
            data-testid="gate-code-input"
            autoFocus
            className={`w-full px-4 py-3 rounded-xl bg-black/60 border-2 ${error ? 'border-red-500 animate-shake' : 'border-gray-600 focus:border-lime-400'} text-white text-center text-lg tracking-widest font-mono outline-none transition-colors`}
          />
          <button
            type="submit"
            data-testid="gate-submit-btn"
            className="w-full mt-4 py-3 rounded-xl font-bold text-black text-sm transition-all duration-300 hover:shadow-[0_0_25px_rgba(132,204,22,0.5)] hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, #84cc16, #06b6d4)' }}
          >
            {t.submit}
          </button>
          {error && <p className="text-red-400 text-xs text-center mt-3 font-medium">{t.invalid}</p>}
        </form>

        {/* NDA Notice */}
        <div className="mt-6 px-4" data-testid="gate-nda-notice">
          <div className="bg-gray-900/60 backdrop-blur-sm border border-lime-500/20 rounded-xl p-4">
            <div className="flex items-start gap-2 mb-2">
              <svg className="w-4 h-4 text-lime-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <p className="text-xs font-semibold text-lime-300 uppercase tracking-wider">{t.confidential}</p>
            </div>
            <p className="text-[11px] text-gray-400 leading-relaxed">{t.nda}</p>
          </div>
          <p className="text-center text-[10px] text-gray-600 mt-3">
            © {new Date().getFullYear()} EcoTech Système — {t.copyright}
          </p>
        </div>
      </div>
    </div>
  );
}

function DemoPortal() {
  const { language } = useLanguage();
  const t = T[language];

  const handleOpenLink = (path) => {
    window.open(path, '_blank');
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(ACCESS_TYPE_KEY);
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative" style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_foodtech-finplan/artifacts/ye5d9g73_image.png')", backgroundSize: '180px', backgroundRepeat: 'repeat', backgroundPosition: 'center' }}>
      <div className="absolute inset-0 bg-black/85" />
      <LangToggle />
      <div className="relative z-10 w-full max-w-2xl">
        <div className="text-center mb-10">
          <img
            src="https://customer-assets.emergentagent.com/job_app-charge/artifacts/151hv25f_EcoTecH_Logo%20short.jpg"
            alt="EcoTech Système"
            className="h-32 mx-auto mb-6 rounded-xl drop-shadow-[0_0_30px_rgba(168,213,48,0.6)]"
          />
          <h1 className="text-2xl font-bold text-white mb-2">{t.portalsTitle}</h1>
          <p className="text-gray-400">{t.portalsSub}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Présentation */}
          <button
            onClick={() => handleOpenLink('/presentation-ecolunch')}
            className="group p-6 bg-gray-900/90 backdrop-blur-md border-2 border-lime-500/30 rounded-2xl hover:border-lime-500 transition-all duration-300 hover:shadow-[0_0_30px_rgba(132,204,22,0.3)] hover:scale-[1.02]"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-r from-lime-400 to-green-500 flex items-center justify-center shadow-[0_0_20px_rgba(132,204,22,0.5)]">
              <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">{t.presentation}</h3>
            <p className="text-sm text-gray-400">{t.presentationSub}</p>
          </button>

          {/* Écosystème */}
          <button
            onClick={() => handleOpenLink('/ecosysteme-portails')}
            className="group p-6 bg-gray-900/90 backdrop-blur-md border-2 border-blue-500/30 rounded-2xl hover:border-blue-500 transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.3)] hover:scale-[1.02]"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.5)]">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">{t.ecosystem}</h3>
            <p className="text-sm text-gray-400">{t.ecosystemSub}</p>
          </button>

          {/* Organigramme — masqué temporairement
          <button
            onClick={() => handleOpenLink('/organigramme')}
            className="group p-6 bg-gray-900/90 backdrop-blur-md border-2 border-cyan-500/30 rounded-2xl hover:border-cyan-500 transition-all duration-300 hover:shadow-[0_0_30px_rgba(34,211,238,0.3)] hover:scale-[1.02]"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-r from-cyan-400 to-teal-500 flex items-center justify-center shadow-[0_0_20px_rgba(34,211,238,0.5)]">
              <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">{t.orgchart}</h3>
          </button>
          */}
        </div>

        {/* Logout button */}
        <div className="mt-8 text-center">
          <button
            onClick={handleLogout}
            className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
          >
            {t.logout}
          </button>
        </div>
      </div>
    </div>
  );
}
